import { Sun, DollarSign, Fuel, Leaf, Lightbulb, Zap, ArrowDownToLine, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { ApexRadialBar } from './charts';
import { useAnimatedValue } from '@/hooks/useAnimatedValue';
import type { EnergyKpiSummary } from '@/types/energy';

interface EnergyKpiStripProps {
  kpis: EnergyKpiSummary;
  days?: number;  // Number of days in selected range
  dateRange?: string;  // Current date range (e.g., '7d', '30d')
  animateValues?: boolean;  // Enable smooth animations for live demo
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  shortLabel?: string;
  value: string | number;
  unit?: string;
  infoContent?: React.ReactNode;
}

function KpiCard({ icon, label, shortLabel, value, unit, infoContent }: KpiCardProps) {
  return (
    <Card className="h-full relative">
      {infoContent && (
        <div className="absolute top-1.5 right-2 z-10">
          <InfoTooltip content={infoContent} />
        </div>
      )}
      <CardContent className="p-3 sm:p-4 h-full flex items-center">
        <div className="flex items-center gap-2 sm:gap-3 w-full">
          <div className="p-2 sm:p-2.5 rounded-lg bg-muted text-muted-foreground shrink-0 [&>svg]:h-6 [&>svg]:w-6">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">
              <span className="sm:hidden">{shortLabel || label}</span>
              <span className="hidden sm:inline">{label}</span>
            </p>
            <p className="text-lg sm:text-2xl font-bold whitespace-nowrap text-right">
              {value}
              {unit && <span className="text-xs sm:text-sm font-normal text-muted-foreground ml-0.5 sm:ml-1">{unit}</span>}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function EnergyKpiStrip({ kpis, days = 30, dateRange = '30d', animateValues = false }: EnergyKpiStripProps) {
  // Animated values for smooth transitions in live demo mode.
  // null = honest absence on prod → the card renders N/A instead of 0.
  const animatedOffGridShare = useAnimatedValue(kpis.offGridEnergyShare ?? 0, { enabled: animateValues });
  const animatedOffGridDays = useAnimatedValue(kpis.fullyOffGridDays ?? 0, { enabled: animateValues });
  const animatedCostReduced = useAnimatedValue(kpis.energyCostReduced ?? 0, { enabled: animateValues });
  const animatedDieselReduced = useAnimatedValue(kpis.dieselGallonsReduced ?? 0, { enabled: animateValues });
  const _animatedCO2Reduced = useAnimatedValue(kpis.totalCO2Reduced ?? 0, { enabled: animateValues, precision: 1 });

  // NEW: Animated values for cumulative totals
  const animatedPvKwh = useAnimatedValue(kpis.totalPvKwh ?? 0, { enabled: animateValues });
  const animatedLoadKwh = useAnimatedValue(kpis.totalLoadKwh ?? 0, { enabled: animateValues });
  const animatedGridKwh = useAnimatedValue(kpis.totalGridImportKwh ?? 0, { enabled: animateValues });
  const animatedCO2Kg = useAnimatedValue(kpis.totalCO2Kg ?? (kpis.totalCO2Reduced ?? 0) * 1000, { enabled: animateValues });
  // Calculate average daily solar generation
  const avgDailySolar = (kpis.totalPvKwh ?? 0) / Math.max(days, 1);
  const animatedAvgDailySolar = useAnimatedValue(avgDailySolar, { enabled: animateValues });

  // Format the date range label
  const formatDateRangeLabel = (range: string) => {
    const labels: Record<string, string> = {
      'today': 'Today',
      '48h': '48h',
      '3d': '3d',
      '5d': '5d',
      '7d': '7d',
      '30d': '30d',
      '60d': '60d',
      '90d': '90d',
      'production': 'Prod',
    };
    return labels[range] || range;
  };

  // Check if new cumulative metrics are available
  const hasNewMetrics = kpis.totalPvKwh != null || kpis.totalLoadKwh != null;

  return (
    <div className="space-y-3">
      {/* Primary KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* First KPI with Radial Gauge */}
        <Card className="border-[#8FB560]/30 bg-[#8FB560]/5 h-full relative">
          <div className="absolute top-1.5 right-2 z-10">
            <InfoTooltip
              content={
                <div className="space-y-1">
                  <p className="font-semibold">Off-Grid Energy Share</p>
                  <p>Percentage of total energy consumption that came from solar and battery, not grid or generators.</p>
                  <p className="text-muted-foreground">Higher percentages indicate greater energy independence and sustainability.</p>
                </div>
              }
            />
          </div>
          <CardContent className="p-3 sm:p-4 h-full flex items-center">
            <div className="flex items-center gap-2 sm:gap-3 w-full">
              <ApexRadialBar value={animatedOffGridShare} size={56} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">
                  <span className="sm:hidden">Off-Grid</span>
                  <span className="hidden sm:inline">Off-Grid</span>
                </p>
                <p className="text-lg sm:text-2xl font-bold text-[#8FB560] text-right">
                  {kpis.offGridEnergyShare == null ? 'N/A' : `${Math.round(animatedOffGridShare)}%`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <KpiCard
          icon={<Sun className="h-5 w-5" />}
          label={`Off-Grid Days (${formatDateRangeLabel(dateRange)})`}
          shortLabel="Off-Grid Days"
          value={kpis.fullyOffGridDays == null ? 'N/A' : Math.round(animatedOffGridDays)}
          infoContent={
            <div className="space-y-1">
              <p className="font-semibold">Off-Grid Days</p>
              <p>Number of days the fleet operated fully on solar and battery power without grid or generator use.</p>
              <p className="text-muted-foreground">Complete energy independence for the selected time period.</p>
            </div>
          }
        />
        <KpiCard
          icon={<DollarSign className="h-5 w-5" />}
          label="Cost Saved"
          shortLabel="Cost Saved"
          value={kpis.energyCostReduced == null ? 'N/A' : `$${Math.round(animatedCostReduced).toLocaleString()}`}
          infoContent={
            <div className="space-y-1">
              <p className="font-semibold">Energy Cost Saved</p>
              <p>Money saved by using solar power instead of grid electricity.</p>
              <p className="text-muted-foreground">Solar energy generated, priced at each unit&apos;s grid import rate for that day (Settings → Energy Rates).</p>
            </div>
          }
        />
        <KpiCard
          icon={<Fuel className="h-5 w-5" />}
          label="Diesel Saved"
          shortLabel="Diesel Saved"
          value={kpis.dieselGallonsReduced == null ? 'N/A' : Math.round(animatedDieselReduced).toLocaleString()}
          unit="gal"
          infoContent={
            <div className="space-y-1">
              <p className="font-semibold">Diesel Saved</p>
              <p>Gallons of diesel fuel not consumed thanks to solar energy generation replacing generator runtime.</p>
              <p className="text-muted-foreground">Based on generator efficiency and actual solar energy produced.</p>
            </div>
          }
        />
        <KpiCard
          icon={<Leaf className="h-5 w-5" />}
          label="Fleet CO₂ Reduced"
          shortLabel="CO₂ Saved"
          value={kpis.totalCO2Kg == null && kpis.totalCO2Reduced == null ? 'N/A' : Math.round(animatedCO2Kg).toLocaleString()}
          unit="kg"
          infoContent={
            <div className="space-y-1">
              <p className="font-semibold">Fleet CO₂ Reduced</p>
              <p>Total kilograms of CO₂ emissions prevented by using clean solar energy instead of fossil fuels.</p>
              <p className="text-muted-foreground">Calculated from grid emission factors and diesel combustion reduced.</p>
            </div>
          }
        />

        {/* CTA Card - Suggest a Metric */}
        <a
          href="mailto:support@hollywoodtrucks.com?subject=Dashboard%20Metric%20Suggestion&body=I%20would%20like%20to%20suggest%20a%20new%20metric%20for%20the%20Ecoluxe%20Dashboard%3A%0A%0A"
          className="group"
        >
          <Card className="h-full border-dashed border-2 border-[#8FB560]/40 bg-[#8FB560]/5 hover:border-[#8FB560] hover:bg-[#8FB560]/10 transition-all cursor-pointer">
            <CardContent className="p-3 sm:p-4 h-full flex items-center">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 rounded-lg bg-[#8FB560]/20 text-[#8FB560] group-hover:bg-[#8FB560]/30 transition-colors shrink-0">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs text-[#8FB560] uppercase tracking-wider font-medium">
                    <span className="sm:hidden">Suggest</span>
                    <span className="hidden sm:inline">Suggest a Metric</span>
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    <span className="sm:hidden">Missing something?</span>
                    <span className="hidden sm:inline">What would you like to see?</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </a>
      </div>

      {/* Secondary KPIs Row - Cumulative Energy Totals */}
      {hasNewMetrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={<Sun className="h-5 w-5" />}
            label="Total Solar Generation"
            shortLabel="Solar"
            value={kpis.totalPvKwh == null ? 'N/A' : Math.round(animatedPvKwh).toLocaleString()}
            unit="kWh"
            infoContent={
              <div className="space-y-1">
                <p className="font-semibold">Total Solar Generation</p>
                <p>Cumulative solar energy generated across the entire fleet during the selected period.</p>
                <p className="text-muted-foreground">Measured directly from solar inverters in kilowatt-hours (kWh).</p>
              </div>
            }
          />
          <KpiCard
            icon={<Zap className="h-5 w-5" />}
            label="Total Load"
            shortLabel="Load"
            value={kpis.totalLoadKwh == null ? 'N/A' : Math.round(animatedLoadKwh).toLocaleString()}
            unit="kWh"
            infoContent={
              <div className="space-y-1">
                <p className="font-semibold">Total Load</p>
                <p>Total energy consumed by all units in the fleet, including HVAC, lighting, equipment, and other electrical loads.</p>
                <p className="text-muted-foreground">Aggregate of all power consumption across the selected time period.</p>
              </div>
            }
          />
          <KpiCard
            icon={<ArrowDownToLine className="h-5 w-5" />}
            label="Grid Import"
            shortLabel="Grid In"
            value={kpis.totalGridImportKwh == null ? 'N/A' : Math.round(animatedGridKwh).toLocaleString()}
            unit="kWh"
            infoContent={
              <div className="space-y-1">
                <p className="font-semibold">Grid Import</p>
                <p>Total energy drawn from the electrical grid when solar and battery power was insufficient to meet demand.</p>
                <p className="text-muted-foreground">Lower values indicate better self-sufficiency from renewable sources.</p>
              </div>
            }
          />
          <KpiCard
            icon={<TrendingUp className="h-5 w-5" />}
            label="Avg Daily Solar"
            shortLabel="Avg/Day"
            value={kpis.totalPvKwh == null ? 'N/A' : Math.round(animatedAvgDailySolar).toLocaleString()}
            unit="kWh"
            infoContent={
              <div className="space-y-1">
                <p className="font-semibold">Average Daily Solar Generation</p>
                <p>Average solar energy generated per day across the fleet during the selected time period.</p>
                <p className="text-muted-foreground">Higher values indicate better solar utilization and consistent renewable energy production.</p>
              </div>
            }
          />
        </div>
      )}
    </div>
  );
}
